import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import * as constants from '../constants.js';
import { ACTION_META, ACTION_TYPES } from './workspaceConstants.js';

// Every request in the workspace is session-cookie authenticated, so the same
// pre-configured client is reused instead of building one per call.
const api = axios.create({ withCredentials: true });
const url = (path) => '//' + constants.BASE_URL + path;

const WorkspaceContext = createContext(null);

export const useWorkspace = () => useContext(WorkspaceContext);

// "Ansicht" defaults: buildings, all four Maßnahmen types and the water level
// are on, everything else off.
const DEFAULT_LAYERS = {
  buildings: true,
  waterLevel: true,
  discharge: false,
  flowPathsStatic: false,
  flowPathsAnimated: false,
  actions: true,
  actionTypes: { basin: true, ditch: true, embankment: true, infiltration: true },
};

const EMPTY_ACTIONS = { basin: [], ditch: [], embankment: [], infiltration: [] };

// Which collection endpoint feeds which Maßnahmen type, and how the payload of
// each is shaped. Keeping this in one table means the four types are handled by
// a single code path everywhere below instead of four near-identical branches.
// The `idKey` / `geometryKey` field names come from ACTION_META, which the map
// needs too.
const ACTION_API = {
  basin: {
    list: (scenarioId) => `/getBasinsForRegion/${scenarioId}`,
    listKey: 'basins',
    add: (scenarioId) => `/addBasin/${scenarioId}`,
    addBody: (geometry) => ({ basin_outline: geometry }),
    addIdKey: 'basin_id',
    remove: (id) => `/deleteBasin/${id}`,
    get: (id) => `/getBasin/${id}`,
    update: (id) => `/updateBasin/${id}`,
  },
  ditch: {
    list: (scenarioId) => `/getDitchesForScenario/${scenarioId}`,
    listKey: 'ditches',
    add: (scenarioId) => `/addDitch/${scenarioId}`,
    addBody: (geometry) => ({ ditch_points: geometry }),
    addIdKey: 'ditch_id',
    remove: (id) => `/deleteDitch/${id}`,
    get: (id) => `/getDitch/${id}`,
    update: (id) => `/updateDitch/${id}`,
  },
  embankment: {
    list: (scenarioId) => `/getEmbankementsForScenario/${scenarioId}`,
    listKey: 'embankments',
    add: (scenarioId) => `/addEmbankment/${scenarioId}`,
    addBody: (geometry) => ({ embankment_points: geometry }),
    addIdKey: 'embankment_id',
    remove: (id) => `/deleteEmbankment/${id}`,
    get: (id) => `/getEmbankment/${id}`,
    update: (id) => `/updateEmbankment/${id}`,
  },
  infiltration: {
    list: (scenarioId) => `/getInfiltrationAreasForRegion/${scenarioId}`,
    listKey: 'infiltration_areas',
    add: (scenarioId) => `/addInfiltrationArea/${scenarioId}`,
    addBody: (geometry) => ({
      infiltration_area_outline: geometry,
      infiltration_rate_start: 20,
      infiltration_rate_end: 0,
      decay_rate: 0,
    }),
    addIdKey: 'infiltration_area_id',
    remove: (id) => `/deleteInfiltrationArea/${id}`,
    get: (id) => `/getInfiltrationArea/${id}`,
    update: (id) => `/updateInfiltrationArea/${id}`,
  },
};

ACTION_TYPES.forEach((type) => {
  ACTION_API[type].idKey = ACTION_META[type].idKey;
  ACTION_API[type].geometryKey = ACTION_META[type].geometryKey;
});

export { ACTION_API };

// Maps the hazard map's per-building danger classes onto building ids.
// A server that predates the building_id field in /get_hazard_map returns the
// buildings in the same order as /getBuildingsForRegion -- both walk
// region.buildings_of_region -- so the position serves as a fallback key.
const toDangerClassMap = (hazardBuildings, regionBuildings) => {
  const classes = {};
  (hazardBuildings || []).forEach((building, index) => {
    const id = building.building_id != null
      ? building.building_id
      : regionBuildings[index] && regionBuildings[index].building_id;
    if (id != null) classes[id] = building.danger_class;
  });
  return classes;
};

export const WorkspaceProvider = ({ children }) => {
  const { regionId } = useParams();
  const navigate = useNavigate();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [regionName, setRegionName] = useState('');
  const [center, setCenter] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const buildingsRef = useRef(buildings);
  buildingsRef.current = buildings;

  const [openMenu, setOpenMenu] = useState(null);

  // null | 'draw-building' | 'erase-buildings' | 'draw-<actionType>' | 'erase-actions'
  const [interactionMode, setInteractionMode] = useState(null);
  // Bumped after every finished shape so the drawing handler re-arms itself and
  // several features can be drawn in a row without re-clicking the button.
  const [drawNonce, setDrawNonce] = useState(0);

  const [pendingBuildingDeletions, setPendingBuildingDeletions] = useState([]);
  const [pendingActionDeletions, setPendingActionDeletions] = useState([]);

  const [scenarios, setScenarios] = useState([]);
  // Rain of the "light"/"heavy" presets for this region -- KOSTRA-DWD values
  // in Germany, fixed defaults elsewhere. null until loaded.
  const [rainPresets, setRainPresets] = useState(null);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [actions, setActions] = useState(EMPTY_ACTIONS);
  const [dangerClasses, setDangerClasses] = useState({});
  const [velocityData, setVelocityData] = useState(null);
  // Bumped whenever a computation finishes. The result tile URLs carry it, so
  // the map fetches the new rasters instead of keeping the tiles it already
  // has (or the empty ones from before the first run).
  const [resultsVersion, setResultsVersion] = useState(0);
  // Water level at the last point clicked on the map, shown in a popup there:
  // { lat, lng, status: 'loading' | 'done' | 'error', value } or null.
  const [waterLevelProbe, setWaterLevelProbe] = useState(null);
  const probeRequestRef = useRef(0);
  const waterLevelProbeRef = useRef(waterLevelProbe);
  waterLevelProbeRef.current = waterLevelProbe;

  const [layers, setLayers] = useState(DEFAULT_LAYERS);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState(null);

  // Feature whose properties dialog is open: { type, id, values }.
  const [selectedAction, setSelectedAction] = useState(null);

  const notify = useCallback((message, severity = 'info') => {
    setToast({ message, severity, key: Date.now() });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const activeScenario = useMemo(
    () => scenarios.find((s) => s.id === activeScenarioId) || null,
    [scenarios, activeScenarioId]
  );

  /* ------------------------------------------------------------------ */
  /* region + buildings                                                  */
  /* ------------------------------------------------------------------ */

  const loadBuildings = useCallback(async () => {
    const resp = await api.get(url(`/getBuildingsForRegion/${regionId}`));
    setRegionName(resp.data.name);
    setCenter({ lat: parseFloat(resp.data.center_lat), long: parseFloat(resp.data.center_long) });
    setBuildings(resp.data.buildings);
  }, [regionId]);

  const renameRegion = useCallback(async (name) => {
    try {
      await api.post(url('/renameRegion'), { id: regionId, name });
      setRegionName(name);
      notify('Der Name der Region wurde geändert.', 'success');
    } catch (error) {
      notify('Der Name konnte nicht gespeichert werden.', 'error');
    }
  }, [regionId, notify]);

  const addBuilding = useCallback(async (outline) => {
    try {
      const resp = await api.post(url(`/addBuilding/${regionId}`), { building_outline: outline });
      setBuildings((prev) => [...prev, { building_id: resp.data.building_id, building_outline: outline }]);
    } catch (error) {
      notify('Das Gebäude konnte nicht gespeichert werden.', 'error');
    }
  }, [regionId, notify]);

  const markBuildingForDeletion = useCallback((buildingId) => {
    setPendingBuildingDeletions((prev) => (prev.includes(buildingId) ? prev : [...prev, buildingId]));
  }, []);

  const undoBuildingDeletion = useCallback(() => {
    setPendingBuildingDeletions([]);
    setInteractionMode(null);
  }, []);

  const confirmBuildingDeletion = useCallback(async () => {
    const ids = pendingBuildingDeletions;
    setPendingBuildingDeletions([]);
    setInteractionMode(null);
    if (ids.length === 0) return;
    try {
      await Promise.all(ids.map((id) => api.delete(url(`/deleteBuilding/${id}`))));
      setBuildings((prev) => prev.filter((b) => !ids.includes(b.building_id)));
      notify(
        ids.length === 1 ? 'Das Gebäude wurde gelöscht.' : `${ids.length} Gebäude wurden gelöscht.`,
        'success'
      );
    } catch (error) {
      notify('Das Löschen ist fehlgeschlagen.', 'error');
      loadBuildings();
    }
  }, [pendingBuildingDeletions, notify, loadBuildings]);

  /* ------------------------------------------------------------------ */
  /* scenarios                                                           */
  /* ------------------------------------------------------------------ */

  const loadScenarios = useCallback(async () => {
    const idsResp = await api.get(url(`/get_scenario_ids_for_region/${regionId}`));
    const ids = idsResp.data;
    const infos = await Promise.all(
      ids.map((id) =>
        api.get(url(`/get_scenario_information/${id}`)).then((r) => ({ id, progress: null, ...r.data }))
      )
    );
    setScenarios(infos);
    return infos;
  }, [regionId]);

  useEffect(() => {
    let cancelled = false;
    setRainPresets(null);
    api.get(url(`/get_rain_presets/${regionId}`))
      .then((resp) => { if (!cancelled) setRainPresets(resp.data); })
      .catch((error) => console.error('Error loading rain presets:', error));
    return () => { cancelled = true; };
  }, [regionId]);

  // Re-creates the Maßnahmen of `sourceId` on `targetId`, geometry plus
  // properties. Used by "Szenario duplizieren" and by the copy option in the
  // create dialog; the backend's own /copy_scenario only carries over
  // retention basins, so `types` lets the caller skip those.
  const copyActionsBetweenScenarios = useCallback(async (sourceId, targetId, types = ACTION_TYPES) => {
    for (const type of types) {
      const meta = ACTION_API[type];
      const resp = await api.get(url(meta.list(sourceId)));
      for (const item of resp.data[meta.listKey] || []) {
        const createResp = await api.post(url(meta.add(targetId)), meta.addBody(item[meta.geometryKey]));
        const newId = createResp.data[meta.addIdKey];
        // Depths and infiltration rates live behind their own update
        // endpoints, so the copied geometry has to be followed by its
        // properties.
        const details = await api.get(url(meta.get(item[meta.idKey])));
        if (type === 'infiltration') {
          await api.post(url(meta.update(newId)), {
            infiltration_rate_start: details.data.infiltration_rate_start,
            infiltration_rate_end: details.data.infiltration_rate_end,
            decay_factor: details.data.decay_factor,
          });
        } else {
          await api.post(url(meta.update(newId)), { depth: details.data.depth });
        }
      }
    }
  }, []);

  const createScenario = useCallback(async ({ name, selectedScenario, rainAmount, rainDuration, copyFromId }) => {
    try {
      let createdId;

      // Copying keeps the source's results, so it is worth doing whenever the
      // new scenario would compute to the same thing -- same rain, same
      // preset. Change any of that in the dialog and the source's water
      // levels no longer describe this scenario, so it starts uncomputed.
      const source = copyFromId ? scenarios.find((s) => s.id === copyFromId) : null;
      const keepsRainOfSource = source
        && source.selected_scenario === selectedScenario
        && (selectedScenario !== 'custom'
          || (Number(source.rain_amount) === Number(rainAmount)
            && Number(source.rain_duration) === Number(rainDuration)));

      if (source && keepsRainOfSource) {
        const copyResp = await api.post(url(`/copy_scenario/${copyFromId}`));
        createdId = copyResp.data.scenario_id;
        await api.post(url(`/change_scenario_information/${createdId}`), {
          scenario_name: name,
          rain_amount: null,
          rain_duration: null,
          minimum_time_step: source.minimum_time_step,
          selected_scenario: selectedScenario,
        });
      } else {
        const createResp = await api.post(url('/add_scenario'), {
          name,
          region_id: regionId,
          rain_amount: rainAmount,
          rain_duration: rainDuration,
          selected_scenario: selectedScenario,
        });
        createdId = createResp.data.scenario_id;
        if (source) {
          // Different rain, so the results cannot come along -- but the
          // Maßnahmen still can, which is the other half of "übernehmen".
          await copyActionsBetweenScenarios(copyFromId, createdId);
        }
      }

      await loadScenarios();
      // Straight into the new scenario: creating one is always a prelude to
      // working on it.
      setActiveScenarioId(createdId);
      notify('Das Szenario wurde angelegt.', 'success');
      return createdId;
    } catch (error) {
      notify('Das Szenario konnte nicht angelegt werden.', 'error');
      return null;
    }
  }, [regionId, scenarios, loadScenarios, copyActionsBetweenScenarios, notify]);

  const duplicateScenario = useCallback(async (scenarioId) => {
    try {
      const source = scenarios.find((s) => s.id === scenarioId);
      // The backend copies everything -- Maßnahmen, the result rasters and the
      // building danger classes -- so a duplicate of a computed scenario can
      // be displayed immediately.
      const resp = await api.post(url(`/copy_scenario/${scenarioId}`));
      await loadScenarios();
      setActiveScenarioId(resp.data.scenario_id);
      notify(`\u201E${source ? source.name : 'Szenario'}\u201C wurde kopiert.`, 'success');
    } catch (error) {
      notify('Das Szenario konnte nicht kopiert werden.', 'error');
    }
  }, [scenarios, loadScenarios, notify]);

  const deleteScenario = useCallback(async (scenarioId) => {
    try {
      await api.post(url('/deleteScenario'), { id: scenarioId });
      if (scenarioId === activeScenarioId) setActiveScenarioId(null);
      await loadScenarios();
      notify('Das Szenario wurde gelöscht.', 'success');
    } catch (error) {
      notify(error.response?.data?.error || 'Das Löschen ist fehlgeschlagen.', 'error');
    }
  }, [activeScenarioId, loadScenarios, notify]);

  const saveScenarioInformation = useCallback(async (scenarioId, values) => {
    try {
      await api.post(url(`/change_scenario_information/${scenarioId}`), {
        scenario_name: values.name,
        rain_amount: values.selectedScenario === 'custom' ? values.rainAmount : null,
        rain_duration: values.selectedScenario === 'custom' ? values.rainDuration : null,
        minimum_time_step: values.minimumTimeStep,
        selected_scenario: values.selectedScenario,
      });
      await loadScenarios();
      notify('Die Änderungen wurden gespeichert.', 'success');
    } catch (error) {
      notify(error.response?.data?.error || 'Die Änderungen konnten nicht gespeichert werden.', 'error');
    }
  }, [loadScenarios, notify]);

  const updateComputationMethod = useCallback(async (scenarioId, method) => {
    setScenarios((prev) => prev.map((s) => (s.id === scenarioId ? { ...s, computation_method: method } : s)));
    try {
      await api.post(url(`/update_computation_method/${scenarioId}`), { computation_method: method });
    } catch (error) {
      notify('Die Berechnungsmethode konnte nicht gespeichert werden.', 'error');
    }
  }, [notify]);

  const updateAreaExtensionMethod = useCallback(async (scenarioId, method) => {
    setScenarios((prev) => prev.map((s) => (s.id === scenarioId ? { ...s, area_extension_method: method } : s)));
    try {
      await api.post(url(`/update_area_extension_method/${scenarioId}`), { area_extension_method: method });
    } catch (error) {
      notify('Die Einstellung konnte nicht gespeichert werden.', 'error');
    }
  }, [notify]);

  const startComputation = useCallback(async (scenarioId) => {
    try {
      await api.post(url(`/compute_hazard_map/${scenarioId}`), {});
      setScenarios((prev) => prev.map((s) => (s.id === scenarioId ? { ...s, status: 'queued', progress: 0 } : s)));
      notify('Die Berechnung wurde gestartet. Der Fortschritt wird im Menü angezeigt.', 'success');
    } catch (error) {
      notify(error.response?.data?.error || 'Die Berechnung konnte nicht gestartet werden.', 'error');
    }
  }, [notify]);

  /* ------------------------------------------------------------------ */
  /* Maßnahmen                                                           */
  /* ------------------------------------------------------------------ */

  const loadActions = useCallback(async (scenarioId) => {
    if (!scenarioId) {
      setActions(EMPTY_ACTIONS);
      return;
    }
    const responses = await Promise.all(
      ACTION_TYPES.map((type) => api.get(url(ACTION_API[type].list(scenarioId))))
    );
    const next = {};
    ACTION_TYPES.forEach((type, index) => {
      next[type] = responses[index].data[ACTION_API[type].listKey] || [];
    });
    setActions(next);
  }, []);

  const addAction = useCallback(async (type, geometry) => {
    if (!activeScenarioId) return;
    const meta = ACTION_API[type];
    try {
      const resp = await api.post(url(meta.add(activeScenarioId)), meta.addBody(geometry));
      const created = { [meta.idKey]: resp.data[meta.addIdKey], [meta.geometryKey]: geometry };
      setActions((prev) => ({ ...prev, [type]: [...prev[type], created] }));
      // A Maßnahme the user just drew has to be visible, even if its layer was
      // switched off in "Ansicht".
      setLayers((prev) => ({
        ...prev,
        actions: true,
        actionTypes: { ...prev.actionTypes, [type]: true },
      }));
    } catch (error) {
      notify('Die Maßnahme konnte nicht gespeichert werden.', 'error');
    }
  }, [activeScenarioId, notify]);

  const markActionForDeletion = useCallback((type, id) => {
    setPendingActionDeletions((prev) =>
      prev.some((entry) => entry.type === type && entry.id === id) ? prev : [...prev, { type, id }]
    );
  }, []);

  const undoActionDeletion = useCallback(() => {
    setPendingActionDeletions([]);
    setInteractionMode(null);
  }, []);

  const confirmActionDeletion = useCallback(async () => {
    const pending = pendingActionDeletions;
    setPendingActionDeletions([]);
    setInteractionMode(null);
    if (pending.length === 0) return;
    try {
      await Promise.all(pending.map(({ type, id }) => api.delete(url(ACTION_API[type].remove(id)))));
      setActions((prev) => {
        const next = { ...prev };
        pending.forEach(({ type, id }) => {
          next[type] = next[type].filter((item) => item[ACTION_API[type].idKey] !== id);
        });
        return next;
      });
      notify(
        pending.length === 1 ? 'Die Maßnahme wurde gelöscht.' : `${pending.length} Maßnahmen wurden gelöscht.`,
        'success'
      );
    } catch (error) {
      notify('Das Löschen ist fehlgeschlagen.', 'error');
      loadActions(activeScenarioId);
    }
  }, [pendingActionDeletions, notify, loadActions, activeScenarioId]);

  const openActionDialog = useCallback(async (type, id) => {
    const meta = ACTION_API[type];
    try {
      const resp = await api.get(url(meta.get(id)));
      const values = type === 'infiltration'
        ? {
            infiltration_rate_start: resp.data.infiltration_rate_start,
            infiltration_rate_end: resp.data.infiltration_rate_end,
            decay_factor: resp.data.decay_factor,
          }
        : { depth: resp.data.depth };
      setSelectedAction({ type, id, values });
    } catch (error) {
      notify('Die Eigenschaften konnten nicht geladen werden.', 'error');
    }
  }, [notify]);

  const updateSelectedActionValues = useCallback((values) => {
    setSelectedAction((prev) => (prev ? { ...prev, values: { ...prev.values, ...values } } : prev));
  }, []);

  const closeActionDialog = useCallback(() => setSelectedAction(null), []);

  const saveSelectedAction = useCallback(async () => {
    if (!selectedAction) return;
    const { type, id, values } = selectedAction;
    const meta = ACTION_API[type];
    try {
      const body = type === 'infiltration'
        ? {
            infiltration_rate_start: parseFloat(values.infiltration_rate_start),
            infiltration_rate_end: parseFloat(values.infiltration_rate_end),
            decay_factor: parseFloat(values.decay_factor),
          }
        : { depth: parseFloat(values.depth) };
      await api.post(url(meta.update(id)), body);
      setActions((prev) => ({
        ...prev,
        [type]: prev[type].map((item) => (item[meta.idKey] === id ? { ...item, ...values } : item)),
      }));
      setSelectedAction(null);
    } catch (error) {
      notify('Die Eigenschaften konnten nicht gespeichert werden.', 'error');
    }
  }, [selectedAction, notify]);

  /* ------------------------------------------------------------------ */
  /* layers / view                                                       */
  /* ------------------------------------------------------------------ */

  const setLayer = useCallback((key, value) => {
    setLayers((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setActionTypeLayer = useCallback((type, value) => {
    setLayers((prev) => ({ ...prev, actionTypes: { ...prev.actionTypes, [type]: value } }));
  }, []);

  const exportPdf = useCallback(async () => {
    if (!activeScenarioId) return;
    const params = new URLSearchParams({
      water: layers.waterLevel,
      discharge: layers.discharge,
      // The PDF is a still image, so the animated layer has no meaning there --
      // either flow-path toggle exports the static mask instead.
      accumulation: layers.flowPathsStatic || layers.flowPathsAnimated,
      buildings: layers.buildings,
      // The Maßnahmen types switched on in "Ansicht", comma-separated; empty
      // when the Maßnahmen layer as a whole is off.
      actions: layers.actions
        ? ACTION_TYPES.filter((type) => layers.actionTypes[type]).join(',')
        : '',
    });
    setExporting(true);
    try {
      const resp = await fetch(
        `//${constants.BASE_URL}/export_hazard_pdf/${activeScenarioId}?${params}`,
        { credentials: 'include' }
      );
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${regionName || activeScenarioId}.pdf`;
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      notify('Der Export ist fehlgeschlagen.', 'error');
    } finally {
      setExporting(false);
    }
  }, [activeScenarioId, layers, regionName, notify]);

  /* ------------------------------------------------------------------ */
  /* interaction modes                                                   */
  /* ------------------------------------------------------------------ */

  const startDrawing = useCallback((kind) => {
    setPendingBuildingDeletions([]);
    setPendingActionDeletions([]);
    setInteractionMode(`draw-${kind}`);
    setDrawNonce((n) => n + 1);
  }, []);

  const startErasing = useCallback((target) => {
    setPendingBuildingDeletions([]);
    setPendingActionDeletions([]);
    setInteractionMode(`erase-${target}`);
  }, []);

  const cancelInteraction = useCallback(() => {
    setPendingBuildingDeletions([]);
    setPendingActionDeletions([]);
    setInteractionMode(null);
  }, []);

  // Signals the drawing handler that a shape was finished and it should re-arm
  // for the next one.
  const rearmDrawing = useCallback(() => setDrawNonce((n) => n + 1), []);

  const toggleMenu = useCallback((menuId) => {
    setOpenMenu((prev) => (prev === menuId ? null : menuId));
  }, []);

  /* ------------------------------------------------------------------ */
  /* effects                                                             */
  /* ------------------------------------------------------------------ */

  useEffect(() => {
    (async () => {
      try {
        const meResp = await api.get(url('/@me'));
        setIsAdmin(meResp.data.admin === true);
        await loadBuildings();
        await loadScenarios();
        setDataLoaded(true);
      } catch (error) {
        console.error('Error loading region workspace:', error);
        navigate('/');
      }
    })();
  }, [loadBuildings, loadScenarios, navigate]);

  // Everything that belongs to a scenario is (re)loaded when the active one
  // changes, and cleared when the last toggle is switched off.
  useEffect(() => {
    cancelInteraction();
    setSelectedAction(null);
    setVelocityData(null);
    setWaterLevelProbe(null);
    if (!activeScenarioId) {
      setActions(EMPTY_ACTIONS);
      setDangerClasses({});
      return;
    }
    (async () => {
      try {
        await loadActions(activeScenarioId);
        const resp = await api.get(url(`/get_hazard_map/${activeScenarioId}`));
        setDangerClasses(toDangerClassMap(resp.data.buildings, buildingsRef.current));
      } catch (error) {
        console.error('Error loading scenario data:', error);
      }
    })();
  }, [activeScenarioId, loadActions, cancelInteraction]);

  // Only the answer to the latest click is kept, so a slow response cannot
  // overwrite the popup of a click that came after it.
  const probeWaterLevel = useCallback(async (lat, lng) => {
    if (!activeScenarioId) return;
    const requestId = probeRequestRef.current + 1;
    probeRequestRef.current = requestId;
    const loading = { lat, lng, status: 'loading', value: null };
    waterLevelProbeRef.current = loading;
    setWaterLevelProbe(loading);
    try {
      const resp = await api.get(url(`/get_water_level_at/${activeScenarioId}`), { params: { lat, lng } });
      if (probeRequestRef.current !== requestId) return;
      setWaterLevelProbe({ lat, lng, status: 'done', value: resp.data.water_level });
    } catch (error) {
      console.error('Error fetching water level:', error);
      if (probeRequestRef.current !== requestId) return;
      setWaterLevelProbe({ lat, lng, status: 'error', value: null });
    }
  }, [activeScenarioId]);

  // Closing a popup clears the probe -- but only if it is still the probe at
  // that popup's position. A popup also closes when the next click replaces it,
  // and that must not wipe out the newer probe.
  const clearWaterLevelProbe = useCallback((lat, lng) => {
    const current = waterLevelProbeRef.current;
    if (!current || current.lat !== lat || current.lng !== lng) return;
    probeRequestRef.current += 1;
    waterLevelProbeRef.current = null;
    setWaterLevelProbe(null);
  }, []);

  // The velocity field is a single sizeable JSON payload rather than tiles, so
  // it is only fetched once the animated Fließwege layer is actually switched on.
  useEffect(() => {
    if (!layers.flowPathsAnimated || !activeScenarioId || velocityData) return;
    (async () => {
      try {
        const resp = await api.get(url(`/get_flow_velocity_field/${activeScenarioId}`));
        setVelocityData(resp.data);
      } catch (error) {
        console.error('Error fetching flow velocity field:', error);
      }
    })();
  }, [layers.flowPathsAnimated, activeScenarioId, velocityData]);

  // Progress polling, only while something is actually running.
  const runningIds = useMemo(
    () => scenarios.filter((s) => s.status === 'queued' || s.status === 'computing').map((s) => s.id),
    [scenarios]
  );
  const runningKey = runningIds.join(',');
  const activeScenarioIdRef = useRef(activeScenarioId);
  activeScenarioIdRef.current = activeScenarioId;

  useEffect(() => {
    if (runningKey === '') return undefined;
    let cancelled = false;
    const ids = runningKey.split(',').map(Number);

    const poll = async () => {
      try {
        const results = await Promise.all(
          ids.map((id) => api.get(url(`/getScenarioStatus/${id}`)).then((r) => ({ id, ...r.data })))
        );
        if (cancelled) return;
        setScenarios((prev) => prev.map((s) => {
          const result = results.find((r) => r.id === s.id);
          if (!result) return s;
          return { ...s, status: result.scenario_status || s.status, progress: result.progress };
        }));
        // Only running scenarios are polled, so a 'done' here is a run that
        // just finished. Its results are reloaded by the effect below -- not
        // here, since the status update above ends this polling effect.
        if (results.some((r) => r.scenario_status === 'done')) {
          setResultsVersion((v) => v + 1);
        }
      } catch (error) {
        console.error('Error polling scenario status:', error);
      }
    };

    poll();
    const intervalId = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [runningKey]);

  // A finished run changes the building danger classes, the velocity field and
  // the rasters, so the active scenario's results are loaded again.
  useEffect(() => {
    const scenarioId = activeScenarioIdRef.current;
    if (resultsVersion === 0 || !scenarioId) return undefined;
    let cancelled = false;
    setVelocityData(null);
    setWaterLevelProbe(null);
    (async () => {
      try {
        const resp = await api.get(url(`/get_hazard_map/${scenarioId}`));
        if (cancelled || scenarioId !== activeScenarioIdRef.current) return;
        setDangerClasses(toDangerClassMap(resp.data.buildings, buildingsRef.current));
      } catch (error) {
        console.error('Error reloading scenario results:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [resultsVersion]);

  const value = {
    regionId,
    regionName,
    renameRegion,
    center,
    dataLoaded,
    isAdmin,

    openMenu,
    setOpenMenu,
    toggleMenu,

    interactionMode,
    drawNonce,
    startDrawing,
    startErasing,
    cancelInteraction,
    rearmDrawing,

    buildings,
    addBuilding,
    pendingBuildingDeletions,
    markBuildingForDeletion,
    confirmBuildingDeletion,
    undoBuildingDeletion,

    scenarios,
    rainPresets,
    activeScenarioId,
    activeScenario,
    setActiveScenarioId,
    createScenario,
    duplicateScenario,
    deleteScenario,
    saveScenarioInformation,
    updateComputationMethod,
    updateAreaExtensionMethod,
    startComputation,

    actions,
    addAction,
    pendingActionDeletions,
    markActionForDeletion,
    confirmActionDeletion,
    undoActionDeletion,
    selectedAction,
    openActionDialog,
    updateSelectedActionValues,
    closeActionDialog,
    saveSelectedAction,

    layers,
    setLayer,
    setActionTypeLayer,

    dangerClasses,
    velocityData,
    resultsVersion,
    waterLevelProbe,
    probeWaterLevel,
    clearWaterLevelProbe,
    exportPdf,
    exporting,

    toast,
    notify,
    dismissToast,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};
